/**
 * SUBSCRIPTION CANCELLER — Torpedo Battery (doc 05 §6). 1×1, 70 alloy, 220 HP,
 * 90 dmg single target, 0.25/s, range 12, 5 ammo a shot, targets the fattest.
 *
 * "For when a fish has too many hit points and not enough humility."
 *
 * Silhouette family: TUBE RACK. A tall narrow cabinet with a bundle of VERTICAL
 * tubes standing proud of its roof — the only tower whose top edge is a comb of
 * separate columns. Yeet is wide and diagonal; this is narrow and vertical, and
 * the black-shape test reads the difference immediately.
 *
 * Signature: warm red arming lamp on the cabinet face.
 *
 * Upgrade motifs (doc 05 3×3):
 *   A · NERD MISSILES     the tubes get taller and gain a heavy warhead collar
 *   B · SPAM FOLDER       more tubes, until the rack is a full battery
 *   C · GROUP PUNISHMENT  blast baffles flare out of the roof
 */

import { tiersOf, type BuildContext, type EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_TOWER } from '../materials.ts';
import { isoDiamondRim, isoPrism, type EntityCanvas } from '../../render/primitives.ts';

export const drawCanceller: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_TOWER);
  const lamp = signatureColor(sig, ctx.state);
  const up = tiersOf(ctx);

  // --- the cabinet -----------------------------------------------------------
  isoPrism(c, { n: 1, inset: 2, height: 2, ramp: r });
  const cabH = 9;
  isoPrism(c, { n: 1, inset: 7, height: cabH, lift: 2, ramp: r });
  const roof = 2 + cabH;
  isoDiamondRim(c.s, c.ax, c.ay - roof, 4, r.edge);
  c.s.fillRect(c.ax - 3, c.ay - roof + 4, 7, 1, r.deep); // service seam
  c.s.fillRect(c.ax - 3, c.ay - roof + 6, 3, 2, r.deep); // hatch

  // --- the tubes: a comb standing on the roof --------------------------------
  const tubeH = 8 + (up.a >= 1 ? 1 : 0);
  const count = 2 + (up.b >= 1 ? 1 : 0) + (up.b >= 2 ? 1 : 0) + (up.b >= 3 ? 2 : 0);
  const spread = count <= 2 ? 4 : count <= 4 ? 6 : 8;
  const tubes: number[] = [];
  for (let k = 0; k < count; k++) {
    tubes.push(Math.round(-spread + (2 * spread * k) / Math.max(1, count - 1)));
  }
  const empty = ctx.state === 'dry';
  const firingTube = ctx.state === 'firing' ? Math.round(ctx.phase * (count - 1)) : -1;
  tubes.forEach((dx, k) => {
    const top = c.ay - roof - tubeH;
    c.s.fillRect(c.ax + dx - 1, top, 3, tubeH, r.left);
    c.s.fillRect(c.ax + dx - 1, top, 3, 1, r.edge);
    // the bore: dark unless this tube is the one that just launched
    c.s.px(c.ax + dx, top + 1, k === firingTube ? sig.hot : empty ? r.deep : r.deep);
    if (up.a >= 2) c.s.fillRect(c.ax + dx - 1, top + 2, 3, 1, r.edge); // warhead collar
  });

  // --- A · NERD MISSILES: a heavy loader on the cabinet flank ---------------
  if (up.a >= 1) c.s.fillRect(c.ax - 7, c.ay - roof + 3, 3, 5, r.left);
  if (up.a >= 3) {
    // "The Bill Comes Due": one oversized tube dominates the rack
    const top = c.ay - roof - tubeH;
    c.s.fillRect(c.ax - 3, top, 7, tubeH, r.left);
    c.s.fillRect(c.ax - 3, top, 7, 1, r.edge);
    c.s.fillRect(c.ax - 2, top + 1, 5, 3, r.deep);
  }

  // --- B · SPAM FOLDER: the rack widens into a battery ----------------------
  if (up.b >= 2) {
    c.s.fillRect(c.ax - spread - 2, c.ay - roof - 1, 2 * spread + 5, 2, r.left);
    c.s.fillRect(c.ax - spread - 2, c.ay - roof - 2, 2 * spread + 5, 1, r.edge);
  }
  if (up.b >= 3) {
    // "Unsubscribe All": feed rails run down both sides of the cabinet
    for (const side of [-1, 1]) c.s.fillRect(c.ax + side * 6 - 1, c.ay - roof + 2, 2, 6, r.left);
  }

  // --- C · GROUP PUNISHMENT: blast baffles ----------------------------------
  if (up.c >= 1) {
    for (const side of [-1, 1]) c.s.fillRect(c.ax + side * 5 - 1, c.ay - roof - 3, 2, 4, r.left);
  }
  if (up.c >= 2) {
    for (const side of [-1, 1]) c.s.fillRect(c.ax + side * 7 - 1, c.ay - roof - 1, 2, 4, r.left);
  }
  if (up.c >= 3) {
    // "Reply-All": the baffles become a flared skirt around the whole roof
    isoDiamondRim(c.s, c.ax, c.ay - roof + 1, 9, r.edge);
    for (const dx of [-8, -4, 4, 8]) c.s.fillRect(c.ax + dx - 1, c.ay - roof - 2, 2, 3, r.left);
  }

  // --- signature + states ----------------------------------------------------
  if (lamp !== null) c.s.fillRect(c.ax - 1, c.ay - roof + 6, 3, 2, lamp);
  if (ctx.state === 'firing') {
    c.s.px(c.ax, c.ay - roof - tubeH - 1, sig.halo);
    c.s.px(c.ax + 2, c.ay - roof - tubeH - 2, sig.halo);
  } else if (ctx.state === 'tracking') {
    // a round rides up the centre tube and the arming lamp goes live
    const top = c.ay - roof - tubeH;
    c.s.fillRect(c.ax - 1, top - 3, 3, 4, r.edge);
    c.s.px(c.ax, top - 4, sig.on);
    c.s.fillRect(c.ax - 1, c.ay - roof + 6, 3, 2, sig.on, 'set');
    c.s.fillRect(c.ax - 4, c.ay - roof + 4, 9, 1, sig.on);
  }
  if (empty) {
    // out of ammo: the hatch stands open and the lamp is dead
    c.s.fillRect(c.ax - 4, c.ay - roof + 5, 9, 3, r.deep, 'set');
  }
  if (ctx.state === 'selected') isoDiamondRim(c.s, c.ax, c.ay - 2, 6, r.edge);
};
